import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import GameRouter from './components/GameRouter';
import ModeSelect from './components/ModeSelect';
import { GameMode, Difficulty, GameType } from './types/game';
import { Match3Mode } from './utils/match3';
import './App.css';

function App() {
  const [gameMode, setGameMode] = useState<GameMode | null>(null);
  const [difficulty, setDifficulty] = useState<Difficulty | undefined>(undefined);
  const [gameType, setGameType] = useState<GameType | null>(null);
  const [selectedGameTypeForModeSelect, setSelectedGameTypeForModeSelect] = useState<GameType | null>(null);
  const [match3Mode, setMatch3Mode] = useState<Match3Mode>('classic');
  const navigate = useNavigate();
  const location = useLocation();

  // 根据URL路径自动选择游戏类型
  useEffect(() => {
    const path = location.pathname;

    // 定义游戏路径映射
    const gamePathMap: Record<string, GameType> = {
      '/gomoku': 'gomoku',
      '/checkers': 'checkers',
      '/reversi': 'reversi',
      '/connectfour': 'connectfour',
      '/xiangqi': 'xiangqi',
      '/tictactoe': 'tictactoe',
      '/jungle': 'jungle',
      '/tigerchicken': 'tigerchicken',
      '/hnefatafl': 'hnefatafl',
      '/straight': 'straight',
      '/tablut': 'tablut',
      '/match3': 'match3',
      '/match3-classic': 'match3',
      '/match3-stars': 'match3',
      '/match3-happy': 'match3',
      '/match3-fruit': 'match3'
    };

    // 根据路径设置游戏类型
    const matchedGameType = gamePathMap[path];
    if (matchedGameType && !selectedGameTypeForModeSelect) {
      setSelectedGameTypeForModeSelect(matchedGameType);
      setGameType(matchedGameType);

      // 对于消消看游戏，设置相应的模式
      if (path === '/match3-stars') {
        setMatch3Mode('stars');
      } else if (path === '/match3-happy') {
        setMatch3Mode('happy');
      } else if (path === '/match3-fruit') {
        setMatch3Mode('fruit');
      } else if (path === '/match3' || path === '/match3-classic') {
        setMatch3Mode('classic');
      }
    }
  }, [location.pathname, selectedGameTypeForModeSelect]);

  const [playerColor, setPlayerColor] = useState<'black' | 'white' | null>(null); // 直棋玩家选择的颜色

  const handleModeSelect = (mode: GameMode, selectedDifficulty?: Difficulty, selectedGameType?: GameType, selectedMatch3Mode?: Match3Mode, selectedPlayerColor?: 'black' | 'white') => {
    setGameMode(mode);
    setDifficulty(selectedDifficulty);
    const finalGameType = selectedGameType || 'gomoku';
    setGameType(finalGameType);
    setSelectedGameTypeForModeSelect(finalGameType);

    // 设置玩家颜色（仅用于直棋人机对战）
    if (finalGameType === 'straight' && mode === 'pvc' && selectedPlayerColor) {
      setPlayerColor(selectedPlayerColor);
    } else {
      setPlayerColor(null);
    }

    if (selectedMatch3Mode) {
      setMatch3Mode(selectedMatch3Mode);
    }

    // 更新URL路径
    if (finalGameType === 'match3') {
      const match3Path = selectedMatch3Mode === 'stars' ? '/match3-stars' :
                        selectedMatch3Mode === 'happy' ? '/match3-happy' :
                        selectedMatch3Mode === 'fruit' ? '/match3-fruit' : '/match3';
      navigate(match3Path);
    } else {
      navigate(`/${finalGameType}`);
    }
  };

  const handleBackToMenu = () => {
    setGameMode(null);
    setDifficulty(undefined);
    setGameType(null);
    setSelectedGameTypeForModeSelect(null);
    setPlayerColor(null);
    navigate('/');
  };

  const handleBackToModeSelect = () => {
    setGameMode(null);
    setDifficulty(undefined);
    // 保持 gameType 和 selectedGameTypeForModeSelect 不变，让用户回到选择游戏模式和AI难度的页面
    if (gameType) {
      navigate(`/${gameType}`);
    }
  };

  const handleBackToGameTypeSelect = () => {
    setSelectedGameTypeForModeSelect(null);
    setGameType(null);
    navigate('/');
  };

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        {/* 游戏特定路径 */}
        <Route path="/gomoku" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/checkers" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/reversi" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/connectfour" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/xiangqi" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/tictactoe" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/jungle" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/tigerchicken" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/hnefatafl" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/straight" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/tablut" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/match3" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/match3-classic" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/match3-stars" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/match3-happy" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />

        <Route path="/match3-fruit" element={
          gameMode && gameType ? (
            <GameRouter
              gameMode={gameMode}
              difficulty={difficulty}
              gameType={gameType}
              match3Mode={match3Mode}
              playerColor={playerColor}
              onBackToMenu={handleBackToMenu}
              onBackToModeSelect={handleBackToModeSelect}
            />
          ) : (
            <ModeSelect
              onModeSelect={handleModeSelect}
              selectedGameType={selectedGameTypeForModeSelect}
              onBackToGameTypeSelect={handleBackToGameTypeSelect}
            />
          )
        } />
      </Routes>
    </div>
  );
}

export default App;